import { computed, type Ref, toValue } from 'vue';
import { useQuery, type UseQueryReturnType } from '@tanstack/vue-query';
import type { CompanySettings } from '@/types/deepsign/CompanySettings';
import { useApi } from '@/composables/useApi.ts';

interface UseOrganizationSettingsQueryParams {
  documentId: Ref<string | null | undefined>;
  enabled: Ref<boolean>;
}

export function useOrganizationSettingsQuery(
  params: UseOrganizationSettingsQueryParams
): UseQueryReturnType<CompanySettings, Error> {
  const { deepSignAPI } = useApi();
  const queryKey = computed(
    () => ['organization', toValue(params.documentId), 'settings'] as const
  );

  const query = useQuery<CompanySettings, Error>({
    queryKey,
    enabled: params.enabled,
    queryFn: async ({ signal }) => {
      const id = toValue(params.documentId);
      if (!id) throw new Error('Missing document id');
      const res =
        await deepSignAPI.value.documentsCompanySettings.getCompanySettings(
          id,
          { signal }
        );
      const data = res?.data as CompanySettings | undefined;
      if (!data) throw new Error('No company settings data');
      return data;
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

  return query;
}
